import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import apiService, { toImageSrc, toThumbSrc, Product } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { useSEO } from '../hooks/useSEO';
import ProductPreviewText from '../components/ProductPreviewText';
import DiscountBadge from '../components/DiscountBadge';
import analytics from '../services/analytics';

const ProductsWrapper = styled.div`
  padding: var(--spacing-8) 0;
  min-height: 60vh;
`;

const Header = styled.div`
  margin-bottom: var(--spacing-8);
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-weight: 600;
  margin-bottom: var(--spacing-4);
  color: var(--color-secondary);
`;

const FilterSection = styled.div`
  margin-bottom: var(--spacing-12);
`;

const ViewControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-8);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--spacing-4);
    align-items: stretch;
  }
`;

const ProductCount = styled.div`
  color: var(--color-text-light);
  font-size: var(--font-size-sm);
  font-weight: 500;
`;

const ViewToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
`;

const ViewToggleLabel = styled.span`
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-weight: 500;
`;

const ViewToggle = styled.div`
  display: flex;
  background: var(--color-primary);
  border-radius: var(--border-radius-lg);
  padding: 4px;
  box-shadow: var(--shadow-sm);
`;

const ViewButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-4);
  border: none;
  border-radius: var(--border-radius);
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? 'var(--color-text)' : 'rgba(255, 255, 255, 0.7)'};
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.active ? 'var(--shadow-sm)' : 'none'};
  
  &:hover {
    background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.active ? 'var(--color-text)' : 'white'};
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-8);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-3);
  }
`;

const CategoryCard = styled.button<{ active?: boolean }>`
  padding: 0;
  border: 3px solid ${props => props.active ? 'var(--color-secondary)' : 'transparent'};
  background-color: white;
  color: var(--color-text);
  border-radius: var(--border-radius-xl);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: ${props => props.active ? 'var(--shadow-lg)' : 'var(--shadow)'};
  overflow: hidden;
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: var(--color-secondary);
  }
  
  .image-container {
    width: 100%;
    height: 160px;
    overflow: hidden;
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
    position: relative;
  }
  
  .category-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover .category-image {
    transform: scale(1.05);
  }
  
  .emoji {
    font-size: 2.5rem;
    margin: var(--spacing-4);
    display: flex;
    align-items: center;
    justify-content: center;
    height: 160px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    width: 100%;
  }
  
  .content {
    padding: var(--spacing-4);
    width: 100%;
  }
  
  .name {
    font-weight: 600;
    font-size: var(--font-size-base);
    margin-bottom: var(--spacing-1);
    color: ${props => props.active ? 'var(--color-secondary)' : 'var(--color-text)'};
  }
  
  .count {
    font-size: var(--font-size-xs);
    opacity: 0.7;
    color: var(--color-text-light);
  }
  
  ${props => props.active && `
    .name {
      color: var(--color-secondary);
      font-weight: 700;
    }
  `}
`;

const SearchSection = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: var(--spacing-8) var(--spacing-6);
  border-radius: var(--border-radius-xl);
  margin-bottom: var(--spacing-12);
  text-align: center;
  box-shadow: var(--shadow-sm);
`;

const SearchTitle = styled.h3`
  color: var(--color-secondary);
  font-size: var(--font-size-lg);
  font-weight: 600;
  margin-bottom: var(--spacing-4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
`;

const IntroParagraph = styled.p`
  margin-top: var(--spacing-2);
  color: var(--color-text-light);
  max-width: 780px;
  line-height: 1.7;
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 500px;
  margin: 0 auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: var(--spacing-4) var(--spacing-6);
  padding-left: 3.5rem;
  border: 3px solid var(--color-border);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-lg);
  background: white;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  
  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 8px 30px rgba(99, 102, 241, 0.15);
    transform: translateY(-2px);
  }
  
  &::placeholder {
    color: var(--color-text-light);
    font-size: var(--font-size-base);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: var(--spacing-4);
  top: 50%;
  transform: translateY(-50%);
  font-size: var(--font-size-xl);
  color: var(--color-text-light);
  pointer-events: none;
  transition: color 0.3s ease;
  
  ${SearchInput}:focus + & {
    color: var(--color-secondary);
  }
`;

const ProductGrid = styled.div<{ viewMode: 'grid' | 'compact' }>`
  display: grid;
  grid-template-columns: ${props => props.viewMode === 'compact' 
    ? 'repeat(auto-fill, minmax(200px, 1fr))' 
    : 'repeat(auto-fill, minmax(280px, 1fr))'
  };
  gap: ${props => props.viewMode === 'compact' 
    ? 'var(--spacing-4)' 
    : 'var(--spacing-6)'
  };
`;

const ProductCardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  text-decoration: none;
  color: inherit;
  flex: 1;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow);
  overflow: hidden;
  height: 100%;
`;

const ProductImage = styled.div`
  aspect-ratio: 1 / 1;
  width: 100%;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  overflow: hidden;
  position: relative;
  padding: var(--spacing-2);
  box-sizing: border-box;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
  }
  
  .discount-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
  }
`;

const ProductInfo = styled.div`
  padding: var(--spacing-4);
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ProductName = styled.h3`
  font-weight: 600;
  margin-bottom: var(--spacing-2);
  font-size: var(--font-size-lg);
`;

const ProductDescription = styled.p`
  color: var(--color-text-light);
  margin-bottom: var(--spacing-3);
  font-size: var(--font-size-sm);
  line-height: 1.5;
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
`;

const Price = styled.span<{ isDiscounted?: boolean }>`
  font-weight: 600;
  color: ${props => props.isDiscounted ? '#28a745' : 'var(--color-secondary)'};
  font-size: var(--font-size-lg);
`;

const ComparePrice = styled.span`
  text-decoration: line-through;
  color: var(--color-text-light);
  font-size: var(--font-size-sm);
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: var(--font-size-lg);
  color: var(--color-text-light);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-16);
  color: var(--color-text-light);
`;

const CardActions = styled.div`
  padding: var(--spacing-4);
  padding-top: var(--spacing-3);
  background: white;
  border-top: 1px solid var(--color-border);
  margin-top: auto;
`;

const QuickAddButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  background: linear-gradient(135deg, var(--color-secondary) 0%, #1f1e3a 100%);
  color: white;
  padding: var(--spacing-4) var(--spacing-6);
  border: none;
  border-radius: var(--border-radius-full);
  font-weight: 600;
  font-size: var(--font-size-base);
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.1s ease, filter 0.15s ease;
  box-shadow: var(--shadow-sm);

  &:hover {
    box-shadow: var(--shadow);
    filter: saturate(1.05);
  }
  &:active {
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(46,45,77,0.25);
  }
  &:disabled {
    background: var(--color-border);
    color: var(--color-text-light);
    cursor: not-allowed;
    filter: none;
    box-shadow: none;
  }
`;


interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  image_url?: string;
  product_count?: number;
}

const Products: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const { addItem } = useCart();
  const { showToast } = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'compact'>('compact');
  
  const currentCategory = searchParams.get('kategori') || 'alla';
  
  // Find current category object for SEO
  const currentCategoryObject = categories.find(cat => cat.slug === currentCategory);

  // Use SEO hook for dynamic metadata
  useSEO({
    entityType: currentCategoryObject ? 'category' : undefined,
    entityId: currentCategoryObject?.id,
    fallbackTitle: currentCategoryObject ? `${currentCategoryObject.name} - Lillaflora` : 'Produkter - Lillaflora',
    fallbackDescription: currentCategoryObject?.description || 'Upptäck våra produkter och kategorier på Lillaflora',
    fallbackImage: '/static/images/default-category.jpg'
  });

  const getProductEmoji = (categories: string[]) => {
    if (categories.some(cat => cat.toLowerCase().includes('ljus'))) return '🕯️';
    if (categories.some(cat => cat.toLowerCase().includes('godis'))) return '🍬';
    if (categories.some(cat => cat.toLowerCase().includes('cottage'))) return '☕';
    return '🏺';
  };

  const getCategoryImage = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    
    // Map category names to uploaded images - check most specific first
    if (name.includes('doft') || name.includes('pinnar')) {
      return '/static/kategori_doftljus_doftpinnar.png';
    }
    // Handle specific "Ljus" category (exact match)
    if (name === 'ljus') {
      return '/static/kategori_ljus.png';
    }
    // Handle "Ljuslyktor och dekoration" category
    if (name.includes('lyktor') || name.includes('dekoration')) {
      return '/static/kategori_ljuslyktor_dekoration.png';
    }
    // Other ljus-related categories (fallback)
    if (name.includes('ljus')) {
      return '/static/kategori_ljus.png';
    }
    if (name.includes('washologi')) {
      return '/static/kategori_washologi.png';
    }
    if (name.includes('inredning')) {
      return '/static/kategori_inredning.png';
    }
    if (name.includes('godis') || name.includes('snacks') || name.includes('lyxigt')) {
      return '/static/kategori_lyxigt_godis.png';
    }
    if (name.includes('mugg') || name.includes('kopp') || name.includes('koppar')) {
      return '/static/kategori_muggar_koppar.png';
    }
    if (name.includes('textil') || name.includes('tyg')) {
      return '/static/kategori_textiler.png';
    }
    if (name.includes('dukning') || name.includes('servering')) {
      return '/static/kategori_dukning_servering.png';
    }
    if (name.includes('konst') || name.includes('växt') || name.includes('växter')) {
      return '/static/kategori_konst_vaxter.png';
    }
    if (name.includes('julpynt')) {
      return '/static/kategori_julpynt.png';
    }

    // Default fallback image or null for emoji fallback
    return null;
  };

  const getCategoryEmoji = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    if (name.includes('ljus')) return '🕯️';
    if (name.includes('godis') || name.includes('snacks')) return '🍬';
    if (name.includes('cottage') || name.includes('kopp')) return '☕';
    if (name.includes('dekoration')) return '🏺';
    if (name.includes('blommor') || name.includes('blomm')) return '🌸';
    if (name.includes('begravning')) return '🕊️';
    return '📦'; // Default emoji for other categories
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentCategory, searchTerm]);

  useEffect(() => {
    if (typeof document === 'undefined' || products.length === 0) {
      return;
    }

    const scriptId = 'products-collection-schema';
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.remove();
    }

    const itemList = products.slice(0, 12).map((product, index) => {
      const productUrl = `https://lillaflora.nu/produkter/${product.slug || product.id}`;
      const item: Record<string, string> = {
        '@type': 'Product',
        'name': product.name,
        'url': productUrl
      };
      if (product.images && product.images.length > 0) {
        item.image = toImageSrc(product.images[0]);
      }
      return {
        '@type': 'ListItem',
        'position': index + 1,
        'item': item
      };
    });

    if (itemList.length === 0) {
      return;
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': 'Produkter – Lillaflora',
      'url': 'https://lillaflora.nu/produkter',
      'mainEntity': {
        '@type': 'ItemList',
        'itemListElement': itemList
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.text = JSON.stringify(schema, null, 2);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [products]);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      console.log('Fetching categories from API...');
      const data = await apiService.getCategories({ with_count: true });
      console.log('Categories data received:', data);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params: any = {};
      
      if (currentCategory !== 'alla') {
        params.category = currentCategory;
      }
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      console.log('Fetching products with params:', params);
      const data = await apiService.getProducts(params);
      console.log('Products data received:', data.length, 'products');
      const normalizedProducts: Product[] = Array.isArray(data) ? data : [];
      const inStockProducts = normalizedProducts.filter(product => (product.inventory_quantity ?? 0) > 0);
      setProducts(inStockProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      console.error('Using fallback data due to API error');
      // Fallback data for development
      setProducts([
        { id: 1, name: 'Lavendel Doftljus', slug: 'lavendel-doftljus', description: '', short_description: 'Naturligt lavendeldoftljus för avslappning', price: 149, sku: '', inventory_quantity: 15, is_active: true, is_featured: false, categories: ['Ljus & Doftljus'] },
        { id: 2, name: 'Vintage Tekopp Set', slug: 'vintage-tekopp-set', description: '', short_description: 'Set med 4 vintage tekoppar', price: 299, sku: '', inventory_quantity: 8, is_active: true, is_featured: false, categories: ['Mayas Cottage'] },
        { id: 3, name: 'Gourmet Choklad', slug: 'gourmet-choklad', description: '', short_description: 'Ekologisk premium choklad', price: 89, sku: '', inventory_quantity: 40, is_active: true, is_featured: false, categories: ['Godis & Snacks'] },
        { id: 4, name: 'Keramik Vas', slug: 'keramik-vas', description: '', short_description: 'Modern handgjord keramikvas', price: 199, sku: '', inventory_quantity: 20, is_active: true, is_featured: false, categories: ['Dekorationer'] }
      ]);
    }
    setLoading(false);
  };

  const handleCategoryChange = (slug: string) => {
    if (slug === 'alla') {
      searchParams.delete('kategori');
    } else {
      searchParams.set('kategori', slug);
    }
    setSearchParams(searchParams);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const getCategoryTitle = () => {
    if (currentCategory === 'alla') return 'Alla Produkter';
    const category = categories.find(cat => cat.slug === currentCategory);
    return category?.name || 'Produkter';
  };

  const getCategoryIntro = () => {
    if (!currentCategoryObject) return '';
    switch (currentCategoryObject.slug) {
      case 'doftljus-och-doftpinnar':
        return 'Här samlar vi alla doftljus, doftpinnar och ljuslyktor som skapar den där mjuka, välkomnande stämningen hemma. ' +
          'Välj mellan lugna, pudriga toner och mer fylliga, kryddiga dofter – perfekt för vardagsrum, badrum eller sovrum. ' +
          'Kombinera gärna med våra lyktor och textilier för att bygga ett rum som känns varmt, ombonat och personligt.';
      case 'ljuslyktor-och-dekoration':
        return 'Ljuslyktor, små stilleben och dekorativa detaljer som ger rummet liv. ' +
          'Här hittar du allt från glaslyktor till små figurer och objekt som gör dukningen eller hyllan mer levande. ' +
          'Matcha med doftljus, textilier och konstväxter för en helhet som känns genomtänkt – oavsett om det är för vardag eller helg.';
      case 'lyxigt-godis-och-annat':
        return 'Små överraskningar och lyxiga godsaker som gör vardagen lite roligare – och blir perfekta värdinnegåvor. ' +
          'Välj mellan choklad, karameller och andra delikatesser som gärna får följa med tillsammans med ett ljus eller en liten inredningsdetalj.';
      case 'muggar-och-koppar':
        return 'Muggar och koppar för alla dagens stunder – morgonkaffet, eftermiddagsfikat eller kvällsteet. ' +
          'Här hittar du favoriter från bland annat Mayas Cottage som gärna får stå framme på hyllan även när de inte används.';
      case 'dukning-och-servering':
        return 'Bygg en dukning som känns varm, personlig och inbjudande. ' +
          'I den här kategorin samlar vi brickor, skålar, uppläggningsfat och små detaljer som gör bordet mer levande – från vardagsmiddag till högtid.';
      case 'textiler':
        return 'Mjuka kuddar, plädar och kökstextilier som knyter ihop färger och material i hemmet. ' +
          'Använd textilierna för att snabbt uppdatera känslan i soffan, på sängen eller runt matbordet utan att göra stora förändringar.';
      default:
        return currentCategoryObject.description || '';
    }
  };

  // Create display categories with "Alla Produkter" option
  const displayCategories = [
    { slug: 'alla', name: 'Alla Produkter', emoji: '🛍️', image: '/static/kategori_alla_kategorier.png', product_count: undefined },
    ...categories.map(cat => ({
      slug: cat.slug,
      name: cat.name,
      emoji: getCategoryEmoji(cat.name),
      image: cat.image_url || getCategoryImage(cat.name),
      product_count: cat.product_count
    }))
  ];

  return (
    <ProductsWrapper>
      <div className="container">
        <Header>
          <Title>{getCategoryTitle()}</Title>
          {currentCategory === 'alla' && (
            <IntroParagraph>
              Utforska vårt sortiment från Lilla Floras butik i Kramfors – doftljus, ljuslyktor, textilier, muggar och
              lyxigt godis tillsammans med inredningsdetaljer som gör varje rum mer ombonat. Filtrera på{' '}
              <Link to="/produkter?kategori=doftljus-och-doftpinnar">ljus &amp; doftljus</Link>,{' '}
              <Link to="/produkter?kategori=dukning-och-servering">dukning &amp; servering</Link> eller{' '}
              <Link to="/produkter?kategori=textiler">mjuka textilier</Link> för att hitta den rätta känslan. Behöver du
              presenthjälp? Läs mer i{' '}
              <Link to="/floristbloggen">Floristbloggen</Link> eller kontakta oss direkt för personliga tips.
            </IntroParagraph>
          )}
          {currentCategory !== 'alla' && currentCategoryObject && (
            <IntroParagraph>
              {getCategoryIntro()}
            </IntroParagraph>
          )}
        </Header>
        
        <SearchSection>
          <SearchTitle>
            🔍 Hitta dina favoritprodukter
          </SearchTitle>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Sök efter ljus, godis, koppar eller andra produkter..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <SearchIcon>🔍</SearchIcon>
          </SearchContainer>
        </SearchSection>
        
        <FilterSection>
          <CategoryGrid>
            {displayCategories.map(category => (
              <CategoryCard
                key={category.slug}
                active={currentCategory === category.slug}
                onClick={() => handleCategoryChange(category.slug)}
              >
                {category.image ? (
                  <div className="image-container">
                    <img 
                      src={category.image} 
                      alt={category.name}
                      className="category-image"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="emoji">{category.emoji}</div>
                )}
                <div className="content">
                  <div className="name">{category.name}</div>
                  {category.product_count !== undefined && (
                    <div className="count">{category.product_count} produkter</div>
                  )}
                </div>
              </CategoryCard>
            ))}
            </CategoryGrid>
        </FilterSection>

        {loading ? (
          <LoadingWrapper>
            Laddar produkter...
          </LoadingWrapper>
        ) : products.length > 0 ? (
          <>
            <ViewControls>
              <ProductCount>
                Visar {products.length} produkt{products.length !== 1 ? 'er' : ''}
              </ProductCount>
              <ViewToggleContainer>
                <ViewToggleLabel>Vy:</ViewToggleLabel>
                <ViewToggle>
                  <ViewButton 
                    active={viewMode === 'grid'} 
                    onClick={() => setViewMode('grid')}
                    title="Stor rutnätsvy"
                  >
                    ⊞ Stor
                  </ViewButton>
                  <ViewButton 
                    active={viewMode === 'compact'} 
                    onClick={() => setViewMode('compact')}
                    title="Kompakt rutnätsvy"
                  >
                    ⊟ Kompakt
                  </ViewButton>
                </ViewToggle>
              </ViewToggleContainer>
            </ViewControls>
            <ProductGrid viewMode={viewMode}>
              {products.map(product => {
                const mainImage = product.images && product.images.length > 0 ? product.images[0] : undefined;
                const thumbSrc = mainImage ? toThumbSrc(mainImage) : '';
                const fullSrc = mainImage ? toImageSrc(mainImage) : '';
                const imageSrc = thumbSrc || fullSrc;
                const srcSetValue =
                  mainImage && fullSrc
                    ? `${thumbSrc || fullSrc} 480w, ${fullSrc} 900w`
                    : undefined;
                return (
              <ProductCard key={product.id}>
                <ProductCardLink to={`/produkter/${product.slug || product.id}`}>
                  <ProductImage>
                    {product.images && product.images.length > 0 ? (
                      <img
                        loading="lazy"
                        src={imageSrc || toImageSrc(product.images[0])}
                        srcSet={srcSetValue}
                        sizes="(max-width: 768px) 100vw, 320px"
                        alt={product.name}
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.onerror = null;
                          target.src = toImageSrc(product.images![0]);
                        }}
                      />
                    ) : (
                      getProductEmoji(product.categories || [])
                    )}
                    {product.has_active_discount && product.discount_type && (
                      <div className="discount-badge">
                        <DiscountBadge
                          discountPercentage={product.discount_percentage}
                          discountAmount={product.discount_amount}
                          discountType={product.discount_type}
                          discountLabel={product.discount_label}
                          size={viewMode === 'compact' ? 'small' : 'medium'}
                        />
                      </div>
                    )}
                  </ProductImage>
                  <ProductInfo>
                    <div>
                      <ProductName>{product.name}</ProductName>
                      <ProductPreviewText 
                        text={product.short_description || product.description} 
                        maxLength={viewMode === 'compact' ? 60 : 100} 
                      />
                    </div>
                    <ProductPrice>
                      {product.has_active_discount ? (
                        <>
                          <Price isDiscounted={true}>
                            {product.final_price ? Math.round(product.final_price) : product.price} kr
                          </Price>
                          <ComparePrice>{product.price} kr</ComparePrice>
                        </>
                      ) : (
                        <>
                          <Price>{product.price} kr</Price>
                          {product.compare_price && (
                            <ComparePrice>{product.compare_price} kr</ComparePrice>
                          )}
                        </>
                      )}
                    </ProductPrice>
                  </ProductInfo>
                </ProductCardLink>
                <CardActions>
                  <QuickAddButton
                    disabled={(product.inventory_quantity ?? 0) <= 0}
                    onClick={() => {
                      if ((product.inventory_quantity ?? 0) <= 0) {
                        showToast(`${product.name} är tyvärr slutsåld just nu.`, { type: 'warning' });
                        return;
                      }
                      if (!product.id) {
                        showToast('Produkten kan inte läggas i varukorgen just nu.', { type: 'error' });
                        return;
                      }
                      addItem(product.id, 1);
                      analytics.trackAddToCart(product.id, product.name, 1, Number(product.final_price ?? product.price));
                      showToast(`Lade till 1 st "${product.name}" i kundvagnen`, { type: 'success' });
                    }}
                    aria-label={`Lägg till ${product.name} i varukorgen`}
                  >
                    <span aria-hidden>🛒</span>
                    Lägg till i varukorgen
                  </QuickAddButton>
                </CardActions>
              </ProductCard>
                );
              })}
          </ProductGrid>
          </>
        ) : (
          <EmptyState>
            <h3>Inga produkter hittades</h3>
            <p>Prova att ändra dina sökkriterier eller bläddra bland våra kategorier.</p>
          </EmptyState>
        )}
      </div>
    </ProductsWrapper>
  );
};

export default Products;
